import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import type { GraphEntity, GraphRelation } from '../../services/api';
import type { GraphFilterState } from './GraphFilters';

// Register layout extension once at module level
cytoscape.use(coseBilkent);

interface GraphViewerProps {
  entities:         GraphEntity[];
  relations:        GraphRelation[];
  filters:          GraphFilterState;
  groupByType:      boolean;
  onSelectEntity:   (entity: GraphEntity) => void;
  onSelectRelation: (relation: GraphRelation) => void;
  loading?:         boolean;
}

const ENTITY_COLORS: Record<string, string> = {
  Person:       '#3b82f6',
  Organization: '#8b5cf6',
  Place:        '#10b981',
  Event:        '#f59e0b',
  Concept:      '#14b8a6',
  Value:        '#6b7280',
  Entity:       '#94a3b8',
};

const GROUP_COLORS: Record<string, string> = {
  person:       '#3b82f6',
  organization: '#8b5cf6',
  place:        '#10b981',
  event:        '#f59e0b',
  concept:      '#14b8a6',
  value:        '#6b7280',
};

const GROUP_LABELS: Record<string, string> = {
  person:       'Persone',
  organization: 'Organizzazioni',
  place:        'Luoghi',
  event:        'Eventi',
  concept:      'Concetti',
  value:        'Valori',
};

const RELATION_COLORS: Record<string, string> = {
  WORKS_FOR:    '#818cf8',
  WORKS_WITH:   '#a78bfa',
  LOCATED_IN:   '#34d399',
  INVOLVED_IN:  '#fbbf24',
  ABOUT:        '#60a5fa',
  MENTIONS:     '#94a3b8',
  PUBLISHED_BY: '#f472b6',
  OCCURRED_IN:  '#2dd4bf',
  RELATED_TO:   '#9ca3af',
};

const LEVEL_OPACITY: Record<string, number> = {
  STRONG: 0.9,
  MEDIUM: 0.7,
  WEAK:   0.45,
};

function _capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function strengthToWidth(strength?: number): number {
  if (strength == null) return 1;
  return 1 + strength * 5;
}

function strengthToLevel(strength?: number): 'STRONG' | 'MEDIUM' | 'WEAK' {
  if (strength == null || strength < 0.4) return 'WEAK';
  if (strength >= 0.75) return 'STRONG';
  return 'MEDIUM';
}

function mentionCountToSize(count?: number, relevanceScore?: number): number {
  const base = !count ? 30 : Math.min(80, 20 + Math.log(count + 1) * 12);
  if (relevanceScore != null && relevanceScore >= 0.9) return Math.min(90, base * 1.2);
  return base;
}

function showTooltip(el: HTMLDivElement, x: number, y: number, lines: string[]) {
  el.style.left    = `${x}px`;
  el.style.top     = `${y}px`;
  el.style.display = 'block';
  el.innerHTML     = lines
    .map((l, i) =>
      `<div style="${i === 0 ? 'font-weight:600' : 'color:#d1d5db;margin-top:2px'}">${l}</div>`
    )
    .join('');
}

function hideTooltip(el: HTMLDivElement) {
  el.style.display = 'none';
}

export function GraphViewer({
  entities,
  relations,
  filters,
  groupByType,
  onSelectEntity,
  onSelectRelation,
  loading = false,
}: GraphViewerProps) {
  const cyRef      = useRef<cytoscape.Core | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const filteredRelations = useMemo(
    () => relations.filter((r) => {
      if (r.strength != null && r.strength < filters.minStrength) return false;
      if (r.source_reliability_avg != null && r.source_reliability_avg < filters.minSourceReliability) return false;
      if (filters.levelFilter.length > 0) {
        if (!filters.levelFilter.includes(strengthToLevel(r.strength))) return false;
      }
      if (filters.relationTypes.length > 0 && !filters.relationTypes.includes(r.type)) return false;
      return true;
    }),
    [relations, filters.minStrength, filters.minSourceReliability, filters.levelFilter, filters.relationTypes]
  );

  const visibleEntityKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const e of entities) keys.add(e.entity_key);
    for (const r of filteredRelations) {
      keys.add(r.subject);
      keys.add(r.object);
    }
    return keys;
  }, [entities, filteredRelations]);

  const elements = useMemo(() => {
    const uniqueEntities  = [...new Map(entities.map((e) => [e.entity_key, e])).values()];
    const uniqueRelations = [...new Map(
      filteredRelations.map((r) => [`${r.subject}|${r.type}|${r.object}`, r])
    ).values()];

    const nodeKeySet     = new Set(uniqueEntities.map((e) => e.entity_key));
    const validRelations = uniqueRelations.filter(
      (r) => nodeKeySet.has(r.subject) && nodeKeySet.has(r.object)
    );

    const filteredNodes = uniqueEntities.filter((e) => {
      if (!visibleEntityKeys.has(e.entity_key)) return false;
      if (filters.entityTypes.length > 0) {
        if (!filters.entityTypes.includes(e.entity_type ?? '')) return false;
      }
      return true;
    });

    const nodeRenderSet = new Set(filteredNodes.map((e) => e.entity_key));

    const relCountMap = new Map<string, number>();
    for (const r of validRelations) {
      if (nodeRenderSet.has(r.subject) && nodeRenderSet.has(r.object)) {
        relCountMap.set(r.subject, (relCountMap.get(r.subject) ?? 0) + 1);
        relCountMap.set(r.object,  (relCountMap.get(r.object)  ?? 0) + 1);
      }
    }

    const nodes = filteredNodes.map((e) => {
      const label       = (e.labels ?? [])[0] ?? (e.entity_type ? _capitalize(e.entity_type) : 'Entity');
      const isBridge    = e.isBridge ?? false;
      const typeKey     = e.entity_type?.toLowerCase() ?? '';
      const parentId    = groupByType && typeKey ? `group:${typeKey}` : undefined;

      return {
        data: {
          id:           e.entity_key,
          label:        e.normalized_form || e.entity_key.split(':').slice(1).join(':'),
          color:        ENTITY_COLORS[label] ?? ENTITY_COLORS.Entity,
          size:         mentionCountToSize(e.mention_count, e.relevanceScore),
          entityType:   e.entity_type ?? '',
          entity:       e,
          isBridge,
          borderColor:  isBridge ? '#f59e0b' : 'rgba(255,255,255,0.2)',
          borderWidth:  isBridge ? 4 : 2,
          queryGroup:   e.queryGroup ?? '',
          relations:    relCountMap.get(e.entity_key) ?? 0,
          ...(parentId ? { parent: parentId } : {}),
        },
      };
    });

    const edges = validRelations
      .filter((r) => nodeRenderSet.has(r.subject) && nodeRenderSet.has(r.object))
      .map((r, i) => {
        const level = strengthToLevel(r.strength);
        return {
          data: {
            id:       `edge-${i}-${r.subject}-${r.object}`,
            source:   r.subject,
            target:   r.object,
            label:    r.type,
            width:    strengthToWidth(r.strength),
            color:    RELATION_COLORS[r.type] ?? '#94a3b8',
            opacity:  LEVEL_OPACITY[level],
            dashed:   level === 'WEAK' ? 1 : 0,
            level,
            relation: r,
          },
        };
      });

    if (!groupByType) {
      return [...nodes, ...edges];
    }

    // Compound parent nodes: one per entity type present in the graph
    const presentTypes = new Set(
      nodes.map((n) => n.data.entityType?.toLowerCase()).filter(Boolean) as string[]
    );
    const groupNodes = Array.from(presentTypes).map((type) => ({
      data: {
        id:          `group:${type}`,
        label:       GROUP_LABELS[type] ?? _capitalize(type),
        isGroup:     true,
        borderColor: GROUP_COLORS[type] ?? '#6b7280',
        bgColor:     GROUP_COLORS[type] ?? '#6b7280',
      },
    }));

    return [...groupNodes, ...nodes, ...edges];
  }, [entities, filteredRelations, visibleEntityKeys, filters.entityTypes, groupByType]);

  const stylesheet: cytoscape.Stylesheet[] = useMemo(
    () => [
      {
        selector: 'node',
        style: {
          'background-color': 'data(color)',
          'label':            'data(label)',
          'color':            '#fff',
          'font-size':        '10px',
          'text-valign':      'center',
          'text-halign':      'center',
          'width':            'data(size)',
          'height':           'data(size)',
          'text-max-width':   '80px',
          'text-wrap':        'ellipsis',
          'border-width':     'data(borderWidth)' as unknown as number,
          'border-color':     'data(borderColor)',
        } as cytoscape.Css.Node,
      },
      {
        selector: 'node:selected',
        style: {
          'border-width':    3,
          'border-color':    '#fff',
          'overlay-opacity': 0.15,
        } as cytoscape.Css.Node,
      },
      // Compound (group) container style
      {
        selector: ':parent',
        style: {
          'background-color':        'data(bgColor)',
          'background-opacity':      0.08,
          'border-color':            'data(borderColor)',
          'border-width':            1.5,
          'border-style':            'dashed' as cytoscape.Css.LineStyle,
          'label':                   'data(label)',
          'text-valign':             'top' as cytoscape.Css.NodeTextValign,
          'text-halign':             'center' as cytoscape.Css.NodeTextHalign,
          'font-size':               '11px',
          'color':                   '#9ca3af',
          'font-weight':             600,
          'padding':                 '20px',
          'text-background-color':   '#0d1117',
          'text-background-opacity': 0.75,
          'text-background-padding': '3px',
          'text-background-shape':   'roundrectangle' as cytoscape.Css.BackgroundFit,
        } as cytoscape.Css.Node,
      },
      {
        selector: 'edge',
        style: {
          'line-color':         'data(color)',
          'target-arrow-color': 'data(color)',
          'target-arrow-shape': 'triangle',
          'curve-style':        'bezier',
          'width':              'data(width)',
          'label':              'data(label)',
          'font-size':          '8px',
          'color':              '#d1d5db',
          'text-rotation':      'autorotate',
          'text-margin-y':      -8,
          'opacity':            'data(opacity)' as unknown as number,
        } as cytoscape.Css.Edge,
      },
      {
        selector: 'edge[dashed = 1]',
        style: {
          'line-style': 'dashed',
        } as cytoscape.Css.Edge,
      },
      {
        selector: 'edge:selected',
        style: {
          'overlay-opacity': 0.2,
          'opacity':         1,
        } as cytoscape.Css.Edge,
      },
    ],
    []
  );

  const runLayout = useCallback((cy: cytoscape.Core) => {
    if (cy.width() === 0 || cy.height() === 0) return;
    const hasGroups = cy.nodes(':parent').length > 0;
    cy.layout({
      name:                        'cose-bilkent',
      animate:                     false,
      nodeDimensionsIncludeLabels: true,
      idealEdgeLength:             hasGroups ? 100 : 150,
      nodeRepulsion:               hasGroups ? 8000 : 6000,
      nodeOverlap:                 20,
      padding:                     hasGroups ? 60 : 40,
      nestingFactor:               hasGroups ? 0.1 : 1,
      gravity:                     0.2,
      gravityRangeCompound:        1.5,
      gravityCompound:             1.0,
      edgeElasticity:              0.45,
      numIter:                     2500,
      tile:                        !hasGroups,
      randomize:                   true,
    } as cytoscape.LayoutOptions).run();
    cy.fit(undefined, 40);
  }, []);

  const handleCyInit = useCallback(
    (cy: cytoscape.Core) => {
      cyRef.current = cy;

      cy.on('tap', 'node', (evt) => {
        const data = evt.target.data() as Record<string, unknown>;
        if (data.isGroup) return;
        const entity = data.entity as GraphEntity;
        if (entity) onSelectEntity(entity);
      });

      cy.on('tap', 'edge', (evt) => {
        const relation = evt.target.data('relation') as GraphRelation;
        if (relation) onSelectRelation(relation);
      });

      cy.on('mouseover', 'node', (evt) => {
        const tip = tooltipRef.current;
        if (!tip) return;
        const data = evt.target.data() as Record<string, unknown>;
        if (data.isGroup) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pos  = (evt as any).renderedPosition as { x: number; y: number };
        showTooltip(tip, pos.x + 14, pos.y - 10, [
          data.label      as string,
          data.entityType ? `Tipo: ${data.entityType as string}` : '',
          `Relazioni: ${data.relations as number}`,
          data.isBridge  ? '★ Nodo ponte' : '',
          data.queryGroup && data.queryGroup !== 'bridge' ? `Gruppo: ${data.queryGroup as string}` : '',
        ].filter(Boolean) as string[]);
      });

      cy.on('mouseover', 'edge', (evt) => {
        const tip = tooltipRef.current;
        if (!tip) return;
        const data = evt.target.data() as Record<string, unknown>;
        const rel  = data.relation as GraphRelation | undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pos  = (evt as any).renderedPosition as { x: number; y: number };
        showTooltip(tip, pos.x + 14, pos.y - 10, [
          `Tipo: ${data.label as string}`,
          rel?.strength != null ? `Forza: ${rel.strength.toFixed(2)}` : '',
          `Livello: ${data.level as string}`,
          rel?.documents_count != null ? `Documenti: ${rel.documents_count}` : '',
          rel?.source_reliability_avg != null ? `Affidabilità fonte: ${(rel.source_reliability_avg * 100).toFixed(0)}%` : '',
        ].filter(Boolean) as string[]);
      });

      cy.on('mouseout', 'node', () => {
        const tip = tooltipRef.current;
        if (tip) hideTooltip(tip);
      });

      cy.on('mouseout', 'edge', () => {
        const tip = tooltipRef.current;
        if (tip) hideTooltip(tip);
      });

      cy.on('tap', () => {
        const tip = tooltipRef.current;
        if (tip) hideTooltip(tip);
      });

      setTimeout(() => runLayout(cy), 50);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSelectEntity, onSelectRelation, runLayout]
  );

  useEffect(() => {
    if (cyRef.current && elements.length > 0) {
      setTimeout(() => { if (cyRef.current) runLayout(cyRef.current); }, 50);
    }
  }, [elements, runLayout]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900 rounded-lg">
        <div className="text-gray-400 text-sm">Caricamento grafo…</div>
      </div>
    );
  }

  if (elements.filter((e) => !('source' in e.data) && !e.data.isGroup).length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900 rounded-lg">
        <div className="text-gray-500 text-sm text-center p-8">
          Nessuna entità disponibile.<br />
          <span className="text-xs">Carica documenti e avvia una ricerca.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-gray-900 rounded-lg overflow-hidden">
      <CytoscapeComponent
        elements={elements}
        stylesheet={stylesheet}
        cy={handleCyInit}
        style={{ width: '100%', height: '100%' }}
        minZoom={0.2}
        maxZoom={3}
      />
      <div
        ref={tooltipRef}
        className="pointer-events-none absolute z-10 bg-gray-800/90 border border-gray-600 rounded px-2 py-1.5 text-xs text-white shadow-lg max-w-[200px]"
        style={{ display: 'none', left: 0, top: 0 }}
      />
    </div>
  );
}
